import { Injectable } from '@angular/core';
import { AuthenticatorService } from '@aws-amplify/ui-angular';
import { CognitoIdToken } from 'amazon-cognito-identity-js';
import {
  finalize,
  Observable,
  of,
  ReplaySubject,
  shareReplay,
  timeout,
} from 'rxjs';

const MAX_WAIT_FOR_COGNITO_MS = 250;

// Cognito can leave `refreshSession` hanging without ever invoking its
// callback. Cap the wait so callers get an error instead of a stream that
// never settles — the auth-retry interceptor turns that into a sign-in reload.
const REFRESH_TIMEOUT_MS = 10_000;

@Injectable({
  providedIn: 'root',
})
export class CognitoTokenService {
  // Use ReplaySubject so it doesn't emit before the token is ready,
  // but once it is ready, it'll replay the token for all new subscribers.
  // Emits null when the user is not authenticated through Cognito.
  private idTokenSubject = new ReplaySubject<CognitoIdToken | null>(1);
  public idToken$: Observable<CognitoIdToken | null> =
    this.idTokenSubject.asObservable();

  constructor(private readonly authService: AuthenticatorService) {
    // This is not a regular observable. `subscribe` is technically for internal use only, but see
    // https://github.com/aws-amplify/amplify-ui/issues/2314#issuecomment-1191031571
    authService.subscribe(() => {
      const { authStatus } = authService;
      if (authStatus === 'authenticated') {
        const idToken = this.getIdToken() ?? null;
        this.idTokenSubject.next(idToken);
      }
    });

    // In case the user is not authenticated through Cognito
    // yet, let's make sure we advance the idToken so the
    // frontend isn't stuck waiting for it.
    setTimeout(() => {
      if (authService.authStatus !== 'authenticated') {
        this.idTokenSubject.next(null);
      }
    }, MAX_WAIT_FOR_COGNITO_MS);
  }

  // A page usually has several requests in flight, and an expired session
  // fails all of them at once. Share one refresh between them rather than
  // stampeding Cognito, and clear it when it settles so the next 401 starts a
  // fresh one.
  private inFlightRefresh: Observable<string | null> | null = null;

  // Resolves to the refreshed jwt, or `null` when there is nothing to refresh
  // or the refresh failed — callers act on that (the auth-retry interceptor
  // retries a 401'd request on a jwt and reloads to sign-in on `null`), so
  // every path has to settle. A failure is a `null` value rather than a thrown
  // error: the callback runs on Cognito's stack, where nothing the caller has
  // is able to catch it.
  public refreshToken$(): Observable<string | null> {
    this.inFlightRefresh ??= this.startRefresh().pipe(
      timeout(REFRESH_TIMEOUT_MS),
      finalize(() => (this.inFlightRefresh = null)),
      shareReplay({ bufferSize: 1, refCount: false })
    );
    return this.inFlightRefresh;
  }

  private startRefresh(): Observable<string | null> {
    const user = this.authService.user;
    const refreshToken = user?.getSignInUserSession()?.getRefreshToken();

    if (!user || !refreshToken) {
      this.idTokenSubject.next(null);
      return of(null);
    }

    return new Observable<string | null>((subscriber) => {
      user.refreshSession(refreshToken, (err) => {
        if (err != null) {
          // eslint-disable-next-line no-console
          console.error(`Could not refresh token. ${err}`);
          this.idTokenSubject.next(null);
          subscriber.next(null);
        } else {
          // Keep `idToken$` fed for consumers that watch it rather than
          // calling refreshToken$() themselves, such as the MFA guards.
          const token = this.getIdToken() ?? null;
          this.idTokenSubject.next(token);
          subscriber.next(token?.getJwtToken() ?? null);
        }
        subscriber.complete();
      });
    });
  }

  public isGoogleIdentity(idToken: CognitoIdToken | null): boolean {
    const raw = idToken?.payload?.identities;
    if (!raw) {
      return false;
    }
    try {
      const identities = Array.isArray(raw) ? raw : JSON.parse(raw);
      return (
        Array.isArray(identities) &&
        identities.some(
          (id: { providerName?: string }) =>
            id?.providerName?.toLowerCase() === 'google'
        )
      );
    } catch {
      return false;
    }
  }

  private getIdToken(): CognitoIdToken | undefined {
    const session = this.authService.user?.getSignInUserSession();
    return session?.getIdToken();
  }
}
